import React from 'react';
import SplitProgressBar from './SplitProgressBar';

// The persistent left rail of the workbench: batch progress, the scan field, a
// filter row, and the roster of units in this session.
//
// The scan field lives here rather than in the main pane because after the
// reskin the roster is always on screen — so there is exactly one place a
// serial is ever typed, whether or not a unit is currently open. The parent
// still owns the input ref so focus management is unchanged.
//
// `units` is [{ tmpId, serial, state, tone, selected }].
export default function RosterRail({
  title = 'Manifest', total, passed, failed, checked,
  scanRef, scanValue, onScanInput, onScanSubmit, scanDisabled,
  filters = [], activeFilter, onFilter,
  units = [], onSelect, emptyLabel = 'No units scanned yet',
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      background: 'var(--surface-default)', border: '1px solid var(--border-default)',
      borderRadius: 'var(--radius-md)',
    }}>
      <div style={{
        flexShrink: 0, padding: 'var(--space-3) var(--space-4)',
        borderBottom: '1px solid var(--border-default)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
          <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {checked} of {total} checked
          </span>
        </div>

        <SplitProgressBar
          total={total}
          label={`${checked} of ${total} units checked`}
          segments={[
            { value: passed, color: 'var(--success-text)', label: `${passed} passed` },
            { value: failed, color: 'var(--danger-text)', label: `${failed} failed` },
          ]}
        />

        <form
          onSubmit={e => { e.preventDefault(); onScanSubmit(); }}
          style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <input
            ref={scanRef}
            value={scanValue}
            onChange={e => onScanInput(e.target.value)}
            disabled={scanDisabled}
            aria-label="Scan the next unit"
            placeholder="Scan the next unit"
            className="input"
            style={{ flex: 1, minWidth: 0 }}
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={scanDisabled || !scanValue.trim()}>
            Look up
          </button>
        </form>

        {filters.length > 0 && (
          <div role="group" aria-label="Filter roster" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {filters.map(f => {
              const on = activeFilter === f.value;
              return (
                <button
                  key={f.value} type="button" onClick={() => onFilter(f.value)}
                  aria-pressed={on}
                  style={{
                    display: 'inline-flex', alignItems: 'center', height: 26, padding: '0 10px',
                    borderRadius: 'var(--radius-pill)', fontSize: 12, fontFamily: 'inherit',
                    fontWeight: on ? 600 : 500, cursor: 'pointer',
                    border: `1px solid ${on ? 'var(--brand-border)' : 'var(--border-default)'}`,
                    background: on ? 'var(--brand-tint)' : 'var(--surface-default)',
                    color: on ? 'var(--brand-light)' : 'var(--text-muted)',
                  }}>
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {units.length === 0 ? (
          <div style={{ padding: 'var(--space-4)', fontSize: 13, color: 'var(--text-muted)' }}>{emptyLabel}</div>
        ) : units.map(u => (
          <button
            key={u.tmpId} type="button" onClick={() => onSelect(u.tmpId)}
            aria-current={u.selected ? 'true' : undefined}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              height: 40, padding: '0 var(--space-4)', cursor: 'pointer', textAlign: 'left',
              fontFamily: 'inherit', border: 'none',
              borderBottom: '1px solid var(--border-subtle)',
              background: u.selected ? 'var(--brand-tint)' : 'transparent',
              boxShadow: u.selected ? 'inset 2px 0 0 0 var(--brand-default)' : 'none',
            }}>
            <span className="tnum" style={{
              flex: 1, minWidth: 0, fontSize: 14, fontWeight: u.selected ? 600 : 500,
              color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{u.serial}</span>
            <span className={'pill pill-' + u.tone}>{u.state}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
